import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCalendar, FiCheck, FiChevronDown, FiClock, FiEdit2, FiRefreshCw, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatUtcDateTimeToChicago } from '../../utils/timezoneUtils';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import styles from './MyAttendance.module.css';
import { DateTime } from 'luxon';
import { getCheckedInTimestamp, hasCheckedInRecord } from '../../utils/attendanceSessionStatus';
import AttendanceCorrectionModal from '../../components/attendance/AttendanceCorrectionModal';

const correctionBadgeClass = (status) => {
  switch (status) {
    case 'auto_approved':
    case 'approved':
      return styles.correctionBadgeApproved;
    case 'denied':
      return styles.correctionBadgeDenied;
    case 'pending':
    default:
      return styles.correctionBadgePending;
  }
};

const correctionStatusLabel = (status) => {
  switch (status) {
    case 'auto_approved':
      return 'Auto-approved';
    case 'approved':
      return 'Approved';
    case 'denied':
      return 'Denied';
    case 'pending':
    default:
      return 'Pending review';
  }
};

function MyAttendance({ autoCheckIn = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [courses, setCourses] = useState([]);
  const [reportsByCourse, setReportsByCourse] = useState({});
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportError, setReportError] = useState('');
  const [expandedReports, setExpandedReports] = useState({});
  const [selectedReportCourseId, setSelectedReportCourseId] = useState(null);
  const [reportSearch, setReportSearch] = useState('');

  // Attendance correction requests
  const [correctionsBySession, setCorrectionsBySession] = useState({});
  const [correctionModal, setCorrectionModal] = useState(null); // { sessionId, title, currentStatus }
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  const autoProcessedRef = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) {
      const redirectPath = `${location.pathname}${location.search || ''}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`, { replace: true });
    }
  }, [authLoading, user, navigate, location.pathname, location.search]);

  const applyFlashState = useCallback(() => {
    const flash = location.state?.flash;
    if (!flash) return;

    if (flash.type === 'success') {
      setSuccess(flash.message || 'Attendance updated.');
      setError('');
      setInfo('');
    } else if (flash.type === 'error') {
      setError(flash.message || 'Attendance check-in failed.');
      setSuccess('');
      setInfo('');
    } else {
      setInfo(flash.message || 'Attendance status updated.');
      setError('');
      setSuccess('');
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    applyFlashState();
  }, [applyFlashState]);

  const fetchSessions = useCallback(async () => {
    if (!user || user.role !== 'student') {
      setSessions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await apiService.getActiveAttendanceSessionsToday();
      if (response?.success) {
        setSessions(Array.isArray(response.sessions) ? response.sessions : []);
      } else {
        setError(response?.message || 'Failed to load attendance sessions.');
      }
    } catch (e) {
      logger.error('MyAttendance fetch sessions failed', { error: e });
      setError(e?.message || 'Failed to load attendance sessions.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const fetchCoursesForReport = useCallback(async () => {
    if (!user || user.role !== 'student') {
      return;
    }

    try {
      const response = await apiService.getCourses();
      if (response?.success && Array.isArray(response.courses)) {
        setCourses(response.courses);
      } else {
        setCourses([]);
      }
    } catch (e) {
      logger.error('MyAttendance fetch courses failed', { error: e });
      setCourses([]);
    }
  }, [user]);

  useEffect(() => {
    fetchCoursesForReport();
  }, [fetchCoursesForReport]);

  const checkInSession = useCallback(async (sessionId, { silent = false } = {}) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      if (!silent) {
        setError('');
        setSuccess('');
        setInfo('');
      }

      const response = await apiService.checkInAttendance(sessionId, 'present');
      if (response?.success) {
        if (!silent) {
          setSuccess('Successfully checked in for attendance.');
        }
        await fetchSessions();
        return { success: true, response };
      }

      const msg = response?.message || 'Failed to check in for attendance.';
      if (!silent) {
        setError(msg);
      }
      return { success: false, message: msg };
    } catch (e) {
      logger.error('MyAttendance check-in failed', { error: e, sessionId });
      const msg = e?.message || 'Failed to check in for attendance.';
      if (!silent) {
        setError(msg);
      }
      return { success: false, message: msg };
    } finally {
      setCheckingIn(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  }, [fetchSessions]);

  const visibleSessions = useMemo(() => {
    return (sessions || []).slice().sort((a, b) => {
      const aTime = a?.start_time ? new Date(a.start_time).getTime() : 0;
      const bTime = b?.start_time ? new Date(b.start_time).getTime() : 0;
      return aTime - bTime;
    });
  }, [sessions]);

  const reportCourses = useMemo(() => {
    const source = Array.isArray(courses) ? courses : [];
    return source;
  }, [courses]);

  const reportRows = useMemo(() => {
    const normalizeRate = (value) => Math.max(0, Math.min(100, Number(value) || 0));
    const searchTerm = reportSearch.trim().toLowerCase();

    return reportCourses
      .map((course) => {
        const report = reportsByCourse[course.id];
        const stats = report?.statistics || null;
        const history = report?.history || [];
        const attendanceRate = normalizeRate(stats?.attendanceRate);
        const absentCount = Number(stats?.absentCount) || 0;
        const isActive = course.is_active === 1 || course.is_active === true;
        const statusLevel = attendanceRate >= 85 ? 'good' : attendanceRate >= 70 ? 'warning' : 'danger';
        const needsAttention = Boolean(stats) && attendanceRate < 85;

        return {
          course,
          report,
          stats,
          history,
          attendanceRate,
          absentCount,
          isActive,
          statusLevel,
          needsAttention,
          searchableText: `${course.name || ''} ${course.code || ''}`.toLowerCase()
        };
      })
      .filter(row => !searchTerm || row.searchableText.includes(searchTerm))
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        if (a.stats && b.stats && a.attendanceRate !== b.attendanceRate) return a.attendanceRate - b.attendanceRate;
        if (a.stats !== b.stats) return a.stats ? -1 : 1;
        return String(a.course.name || '').localeCompare(String(b.course.name || ''));
      });
  }, [reportCourses, reportsByCourse, reportSearch]);

  const activeReportRows = useMemo(() => reportRows.filter(row => row.isActive), [reportRows]);
  const previousReportRows = useMemo(() => reportRows.filter(row => !row.isActive), [reportRows]);

  const getWeekKey = useCallback((entry, index) => {
    return `${entry?.session_id ?? 'session'}-${index}`;
  }, []);

  const getDefaultWeekKey = useCallback((history = []) => {
    if (!history.length) return null;
    const indexedHistory = history.map((entry, index) => ({ entry, index }));
    const absences = indexedHistory.filter(({ entry }) => entry?.status === 'absent');
    const source = absences.length ? absences : indexedHistory;
    const latest = source.reduce((best, entry) => {
      const bestTime = best?.entry?.date ? new Date(best.entry.date).getTime() : 0;
      const entryTime = entry?.entry?.date ? new Date(entry.entry.date).getTime() : 0;
      return entryTime >= bestTime ? entry : best;
    }, source[0]);

    return latest ? getWeekKey(latest.entry, latest.index) : null;
  }, [getWeekKey]);

  useEffect(() => {
    if (!selectedReportCourseId) return;
    const selectedRow = reportRows.find(row => String(row.course.id) === String(selectedReportCourseId));
    if (!selectedRow || expandedReports[selectedReportCourseId]) return;

    const defaultWeekKey = getDefaultWeekKey(selectedRow.history);
    if (!defaultWeekKey) return;

    setExpandedReports(prev => ({
      ...prev,
      [selectedReportCourseId]: defaultWeekKey
    }));
  }, [expandedReports, getDefaultWeekKey, reportRows, selectedReportCourseId]);

  const fetchAttendanceReports = useCallback(async () => {
    if (!user || user.role !== 'student') {
      return;
    }

    if (!reportCourses.length) {
      setReportsByCourse({});
      setReportError('');
      return;
    }

    try {
      setLoadingReports(true);
      setReportError('');

      const results = await Promise.all(
        reportCourses.map(async (course) => {
          try {
            const response = await apiService.getStudentAttendance(course.id);
            const stats = response?.attendance?.statistics || null;
            const history = Array.isArray(response?.attendance?.history) ? response.attendance.history : [];

            return [
              course.id,
              {
                course,
                statistics: stats,
                history,
                error: null
              }
            ];
          } catch (e) {
            return [
              course.id,
              {
                course,
                statistics: null,
                history: [],
                error: e?.message || 'Failed to load report'
              }
            ];
          }
        })
      );

      setReportsByCourse(Object.fromEntries(results));
    } catch (e) {
      logger.error('MyAttendance fetch attendance reports failed', { error: e });
      setReportError('Failed to load attendance reports.');
    } finally {
      setLoadingReports(false);
    }
  }, [reportCourses, user]);

  useEffect(() => {
    fetchAttendanceReports();
  }, [fetchAttendanceReports]);

  const fetchCorrections = useCallback(async () => {
    if (!user || user.role !== 'student') {
      return;
    }
    try {
      const response = await apiService.getMyAttendanceCorrections();
      const requests = Array.isArray(response?.requests) ? response.requests : [];
      const map = {};
      // requests come ordered by created_at DESC, so the first seen per session is the latest
      requests.forEach((req) => {
        if (req && req.session_id != null && !map[req.session_id]) {
          map[req.session_id] = req;
        }
      });
      setCorrectionsBySession(map);
    } catch (e) {
      logger.error('MyAttendance fetch corrections failed', { error: e });
    }
  }, [user]);

  useEffect(() => {
    fetchCorrections();
  }, [fetchCorrections]);

  const submitCorrection = useCallback(async () => {
    if (!correctionModal) {
      return;
    }
    const requestedStatus = correctionModal.currentStatus === 'present' ? 'absent' : 'present';
    try {
      setCorrectionSubmitting(true);
      const response = await apiService.requestAttendanceCorrection(
        correctionModal.sessionId,
        requestedStatus,
        correctionReason.trim() || null
      );

      if (response?.success) {
        setError('');
        setInfo('');
        setSuccess(
          response.autoApproved
            ? 'Your attendance was corrected and updated automatically.'
            : 'Your correction request was submitted for admin review.'
        );
        setCorrectionModal(null);
        setCorrectionReason('');
        await Promise.all([fetchCorrections(), fetchAttendanceReports()]);
      } else {
        setError(response?.message || 'Failed to submit correction request.');
      }
    } catch (e) {
      logger.error('MyAttendance submit correction failed', { error: e });
      setError(e?.message || 'Failed to submit correction request.');
    } finally {
      setCorrectionSubmitting(false);
    }
  }, [correctionModal, correctionReason, fetchCorrections, fetchAttendanceReports]);

  useEffect(() => {
    if (!autoCheckIn || autoProcessedRef.current || loading) {
      return;
    }

    autoProcessedRef.current = true;

    const executeAutoFlow = async () => {
      if (visibleSessions.length === 0) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'info', message: 'No active attendance sessions are available right now.' } }
        });
        return;
      }

      if (visibleSessions.length > 1) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'info', message: 'Multiple sessions are available. Select one below to check in.' } }
        });
        return;
      }

      const onlySession = visibleSessions[0];
      const alreadyCheckedIn = hasCheckedInRecord(onlySession);

      if (alreadyCheckedIn) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'success', message: 'You are already checked in for today\'s active session.' } }
        });
        return;
      }

      if (!isWithinCheckinWindow(onlySession)) {
        navigate(`/attendance/checkin/${onlySession.session_id}`, { replace: true });
        return;
      }

      const result = await checkInSession(onlySession.session_id, { silent: true });

      if (result.success) {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'success', message: 'Auto check-in completed successfully.' } }
        });
      } else {
        navigate('/attendance', {
          replace: true,
          state: { flash: { type: 'error', message: result.message || 'Auto check-in failed.' } }
        });
      }
    };

    executeAutoFlow();
  }, [autoCheckIn, checkInSession, loading, navigate, visibleSessions]);

  const getStateChipClass = (session) => {
    if (session?.isActive) return `${styles.chip} ${styles.chipActive}`;
    if (session?.isFuture) return `${styles.chip} ${styles.chipFuture}`;
    return `${styles.chip} ${styles.chipPast}`;
  };

  const getStateLabel = (session) => {
    if (session?.isActive) return 'Active now';
    if (session?.isFuture) return 'Upcoming';
    return 'Ended';
  };

  const getEntityName = (session) => {
    return session?.course_name || session?.elective_name || session?.title || 'Attendance Session';
  };

  const formatDateLabel = (iso) => {
    if (!iso) return 'Date unavailable';

    // Date-only values (and DATE columns serialized at UTC midnight) should not shift by timezone.
    if (typeof iso === 'string') {
      const dateOnlyLike = iso.match(/^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.000)?Z?)?$/);
      if (dateOnlyLike) {
        const dateOnly = DateTime.fromISO(dateOnlyLike[1], { zone: 'America/Chicago' });
        return dateOnly.isValid ? dateOnly.toFormat('cccc, LLL d, yyyy') : 'Date unavailable';
      }
    }

    if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const dateOnly = DateTime.fromISO(iso, { zone: 'America/Chicago' });
      return dateOnly.isValid ? dateOnly.toFormat('cccc, LLL d, yyyy') : 'Date unavailable';
    }

    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone('America/Chicago');
    return dt.isValid ? dt.toFormat('cccc, LLL d, yyyy') : formatUtcDateTimeToChicago(iso);
  };

  const formatTimeLabel = (iso) => {
    if (!iso) return '--';
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone('America/Chicago');
    return dt.isValid ? dt.toFormat('h:mm a') : '--';
  };

  const formatCheckedInTimeLabel = (value) => {
    if (!value) return null;

    const fromIso = DateTime.fromISO(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromIso.isValid) return fromIso.toFormat('h:mm a');

    const fromSql = DateTime.fromSQL(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromSql.isValid) return fromSql.toFormat('h:mm a');

    return null;
  };

  const formatCheckedInDateTimeLabel = (value) => {
    if (!value) return null;

    const fromIso = DateTime.fromISO(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromIso.isValid) return fromIso.toFormat('ccc, LLL d, h:mm a');

    const fromSql = DateTime.fromSQL(value, { zone: 'utc' }).setZone('America/Chicago');
    if (fromSql.isValid) return fromSql.toFormat('ccc, LLL d, h:mm a');

    return null;
  };

  const isWithinCheckinWindow = (session) => {
    if (!session) return false;

    const nowChicago = DateTime.now().setZone('America/Chicago');
    const timeRegex = /^\d{2}:\d{2}(:\d{2})?$/;
    const checkinStart = session?.checkin_start_time || null;
    const checkinEnd = session?.checkin_end_time || null;

    if (typeof checkinStart === 'string' && typeof checkinEnd === 'string' && timeRegex.test(checkinStart) && timeRegex.test(checkinEnd)) {
      const nowClock = nowChicago.toFormat('HH:mm:ss');
      const startClock = checkinStart.length === 5 ? `${checkinStart}:00` : checkinStart;
      const endClock = checkinEnd.length === 5 ? `${checkinEnd}:00` : checkinEnd;
      return nowClock >= startClock && nowClock <= endClock;
    }

    const start = session?.start_time ? DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago') : null;
    const end = session?.end_time ? DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago') : null;

    if (start?.isValid && end?.isValid) {
      return nowChicago >= start && nowChicago <= end;
    }

    return true;
  };

  if (!user && !authLoading) {
    return null;
  }

  return (
    <div className={styles.container}>
      <section className={styles.heroRow}>
        <div className={styles.hero}>
          <div className={styles.heroEyebrow}>{DateTime.now().setZone('America/Chicago').toFormat('cccc, LLLL d')}</div>
          <h1 className={styles.heroTitle}>My Attendance</h1>
          <p className={styles.heroSub}>Check in to active sessions and review your course attendance history.</p>
        </div>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={fetchSessions}
          disabled={loading}
        >
          <FiRefreshCw size={14} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </section>

      {reportCourses.length > 0 && (
        <div className={styles.reportFilters}>
          <div className={styles.reportSearchWrap}>
            <FiSearch className={styles.reportSearchIcon} size={15} aria-hidden="true" />
            <input
              type="search"
              className={styles.reportSearch}
              value={reportSearch}
              onChange={(e) => setReportSearch(e.target.value)}
              placeholder="Search courses"
              aria-label="Search attendance courses"
            />
          </div>
        </div>
      )}

      <div className={styles.statusStack} aria-live="polite" aria-atomic="true">
        {success && <div className={`${styles.status} ${styles.statusSuccess}`}><FiCheck size={16} />{success}</div>}
        {info && <div className={`${styles.status} ${styles.statusInfo}`}><FiClock size={16} />{info}</div>}
        {error && <div className={`${styles.status} ${styles.statusError}`}><FiX size={16} />{error}</div>}
      </div>

      {(loading || visibleSessions.length > 0) ? (
        <section className={styles.activeSection}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>Active Sessions</h2>
            </div>
          </div>

          {loading ? (
            <div className={styles.empty}><strong>Loading sessions</strong><span>Checking for available attendance sessions.</span></div>
          ) : (
            visibleSessions.map((session) => {
            const alreadyCheckedIn = hasCheckedInRecord(session);
            const checkedInTimeLabel = formatCheckedInTimeLabel(getCheckedInTimestamp(session));
            const disabled = checkingIn.has(session.session_id) || alreadyCheckedIn || session?.isPast;

            return (
              <article className={styles.card} key={session.session_id}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.cardTitle}>{session?.title || getEntityName(session)}</h3>
                    <p className={styles.cardSubtitle}>{session?.entity_type === 'elective' ? 'Elective' : 'Course'}{session?.course_name || session?.elective_name ? ` · ${session.course_name || session.elective_name}` : ''}</p>
                  </div>
                  <span className={`${getStateChipClass(session)} ${styles.metaStatusChip}`}>{getStateLabel(session)}</span>
                </div>
                <div className={styles.metaRow}>
                  <div className={styles.metaItem}>
                    <FiCalendar size={14} />
                    {formatDateLabel(session.start_time)}
                  </div>
                  <span className={styles.metaSeparator}>•</span>
                  <div className={styles.metaItem}>
                    <FiUser size={14} />
                    {session?.instructors || `${session?.instructor_first_name || ''} ${session?.instructor_last_name || ''}`.trim() || 'Instructor'}
                  </div>
                </div>

                <div className={styles.timeWindow}>
                  <div className={styles.timeWindowText}>
                    <FiClock size={14} />
                    <span className={styles.timeWindowLabel}>Check-in window:</span>
                    <span className={styles.timeWindowValue}>
                      {formatTimeLabel(session.start_time)} - {formatTimeLabel(session.end_time)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.checkInBtn} ${alreadyCheckedIn ? styles.checkInBtnSuccess : ''}`}
                    disabled={disabled}
                    onClick={() => checkInSession(session.session_id)}
                  >
                    <FiCheck size={14} />
                    {checkingIn.has(session.session_id)
                      ? 'Checking in...'
                      : alreadyCheckedIn
                        ? checkedInTimeLabel
                          ? `Checked In • ${checkedInTimeLabel}`
                          : 'Checked In'
                        : session?.isPast
                          ? 'Check In Closed'
                          : 'Check In'}
                  </button>
                </div>
              </article>
            );
            })
          )}
        </section>
      ) : null}

      <section className={styles.reportSection}>
        {reportError && <div className={`${styles.status} ${styles.statusError}`} aria-live="polite"><FiX size={16} />{reportError}</div>}

        {loadingReports ? (
          <div className={styles.empty}><strong>Loading reports</strong><span>Preparing your attendance statistics.</span></div>
        ) : reportCourses.length === 0 ? (
          <div className={styles.empty}><strong>No courses available</strong><span>Attendance reporting will appear once you are enrolled in a course.</span></div>
        ) : reportRows.length === 0 ? (
          <div className={styles.empty}><strong>No matching courses</strong><span>Adjust the search to see more courses.</span></div>
        ) : (() => {
          const renderReportRows = (rows) => rows.map((row) => {
            const { course, report, stats, history, attendanceRate, absentCount } = row;
            const selectedWeekKey = expandedReports[course.id];
            const selectedWeekIndex = history.findIndex((entry, index) => getWeekKey(entry, index) === selectedWeekKey);
            const selectedEntry = selectedWeekIndex >= 0 ? history[selectedWeekIndex] : null;
            const isSelectedCourse = String(selectedReportCourseId) === String(course.id);
            return (
              <article key={course.id} className={`${styles.reportCourseItem} ${isSelectedCourse ? styles.reportCourseItemSelected : ''}`}>
                <button
                  type="button"
                  className={styles.reportCourseCard}
                  aria-expanded={isSelectedCourse}
                  onClick={() => {
                    setSelectedReportCourseId(prev => String(prev) === String(course.id) ? null : course.id);
                    if (!isSelectedCourse && !expandedReports[course.id]) {
                      const defaultWeekKey = getDefaultWeekKey(history);
                      if (defaultWeekKey) {
                        setExpandedReports(prev => ({
                          ...prev,
                          [course.id]: defaultWeekKey
                        }));
                      }
                    }
                  }}
                >
                  <span
                    className={styles.courseMiniRing}
                    style={{ '--attendance-rate': attendanceRate }}
                    aria-hidden="true"
                  >
                    <span>{stats ? `${attendanceRate}%` : '--'}</span>
                  </span>
                  <span className={styles.reportCourseCardMain}>
                    <span className={styles.reportCourseName}>{course.name}</span>
                    <span className={styles.reportCourseMeta}>
                      {stats
                        ? `${course.code || 'Course'} · ${absentCount} ${absentCount === 1 ? 'absence' : 'absences'}`
                        : report?.error
                          ? `${course.code || 'Course'} · Unable to load`
                          : `${course.code || 'Course'} · No data yet`}
                    </span>
                  </span>
                  <FiChevronDown className={`${styles.courseChevron} ${isSelectedCourse ? styles.courseChevronExpanded : ''}`} size={16} aria-hidden="true" />
                </button>

                {isSelectedCourse ? (
                  <div className={styles.reportDetailPanel}>
                    {report?.error ? (
                      <div className={`${styles.status} ${styles.statusError}`}><FiX size={16} />{report.error}</div>
                    ) : stats ? (
                      <>
                        <div className={styles.reportSummary}>
                          <div
                            className={styles.progressRing}
                            style={{ '--attendance-rate': attendanceRate }}
                            aria-label={`Attendance rate ${attendanceRate}%`}
                          >
                            <div className={styles.progressRingInner}>
                              <strong>{attendanceRate}%</strong>
                            </div>
                          </div>

                          <div className={styles.reportNumbers}>
                            <span><strong>{stats.totalSessions ?? 0}</strong> total</span>
                            <span><strong>{stats.presentCount ?? 0}</strong> present</span>
                            <span><strong>{stats.absentCount ?? 0}</strong> absent</span>
                          </div>
                        </div>

                        <div className={styles.weekStripSection}>
                          <div className={styles.weekStripHeader}>
                            <span>Weekly attendance</span>
                            <span>{history.length} {history.length === 1 ? 'week' : 'weeks'}</span>
                          </div>

                          {history.length === 0 ? (
                            <div className={styles.empty}><strong>No session records</strong><span>No session-level attendance records were found for this course.</span></div>
                          ) : (
                            <>
                              <div className={styles.weekStrip} role="list" aria-label={`${course.name} weekly attendance`}>
                                {history.map((entry, index) => {
                                  const statusValue = entry?.status || 'unknown';
                                  const weekKey = getWeekKey(entry, index);
                                  const isSelected = selectedWeekKey === weekKey;
                                  const weekClass = statusValue === 'present'
                                    ? styles.weekSquarePresent
                                    : statusValue === 'absent'
                                      ? styles.weekSquareAbsent
                                      : styles.weekSquareNeutral;

                                  return (
                                    <button
                                      key={`${course.id}-${entry.session_id}`}
                                      type="button"
                                      className={`${styles.weekSquare} ${weekClass} ${isSelected ? styles.weekSquareSelected : ''}`}
                                      aria-pressed={isSelected}
                                      aria-label={`Week ${index + 1}, ${statusValue}, ${formatDateLabel(entry.date)}`}
                                      onKeyDown={(event) => {
                                        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
                                        event.preventDefault();
                                        const direction = event.key === 'ArrowRight' ? 1 : -1;
                                        const nextIndex = (index + direction + history.length) % history.length;
                                        const nextEntry = history[nextIndex];
                                        if (!nextEntry) return;
                                        setExpandedReports(prev => ({
                                          ...prev,
                                          [course.id]: getWeekKey(nextEntry, nextIndex)
                                        }));
                                        const nextButton = event.currentTarget.parentElement?.querySelector(`[data-week-index="${nextIndex}"]`);
                                        nextButton?.focus();
                                      }}
                                      data-week-index={index}
                                      onClick={() => setExpandedReports(prev => ({
                                        ...prev,
                                        [course.id]: isSelected ? null : weekKey
                                      }))}
                                    >
                                      {index + 1}
                                    </button>
                                  );
                                })}
                              </div>

                              {selectedEntry ? (() => {
                                const statusValue = selectedEntry?.status || 'unknown';
                                const checkedInTimeLabel = statusValue === 'present'
                                  ? formatCheckedInDateTimeLabel(selectedEntry?.last_checkin || selectedEntry?.checked_in_at_utc || selectedEntry?.updated_at)
                                  : null;
                                const correction = correctionsBySession[selectedEntry.session_id];
                                const canRequest = statusValue === 'absent';
                                const pendingCorrection = correction && correction.status === 'pending';
                                const weekIndex = selectedWeekIndex;

                                return (
                                  <div className={styles.weekDetail}>
                                    <div className={styles.weekDetailMain}>
                                      <div className={styles.weekDetailText}>
                                        <span className={styles.reportSessionTitle}>Week {weekIndex >= 0 ? weekIndex + 1 : ''}</span>
                                        <span className={styles.weekDetailSeparator}>·</span>
                                        <span className={styles.reportSessionMeta}>
                                          {formatDateLabel(selectedEntry.date)}
                                        </span>
                                        {checkedInTimeLabel ? (
                                          <span className={styles.checkedInText}>
                                            <FiCheck size={13} />
                                            Checked in on {checkedInTimeLabel}
                                            {correction && correction.status !== 'pending' ? (
                                              <span className={`${styles.correctionBadge} ${correctionBadgeClass(correction.status)}`}>
                                                {correctionStatusLabel(correction.status)}
                                              </span>
                                            ) : null}
                                          </span>
                                        ) : null}
                                        <span className={styles.weekDetailActionRow}>
                                          {statusValue === 'absent' ? (
                                            <span className={styles.notPresentText}>
                                              <FiX size={13} />
                                              Absent
                                            </span>
                                          ) : null}
                                          {pendingCorrection ? (
                                            <span className={styles.pendingReviewText}>
                                              Pending review
                                            </span>
                                          ) : correction && !checkedInTimeLabel ? (
                                            <span className={`${styles.correctionBadge} ${correctionBadgeClass(correction.status)}`}>
                                              {correctionStatusLabel(correction.status)}
                                            </span>
                                          ) : canRequest ? (
                                            <button
                                              type="button"
                                              className={styles.correctionRequestButton}
                                              onClick={() => {
                                                setCorrectionReason('');
                                                setCorrectionModal({
                                                  sessionId: selectedEntry.session_id,
                                                  title: selectedEntry.title || 'Session',
                                                  date: selectedEntry.date,
                                                  currentStatus: statusValue,
                                                });
                                              }}
                                            >
                                              <FiEdit2 size={13} />
                                              Request Correction
                                            </button>
                                          ) : null}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })() : null}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className={styles.empty}><strong>No attendance data</strong><span>Attendance statistics are not available for this class yet.</span></div>
                    )}
                  </div>
                ) : null}
              </article>
            );
          });

          return (
            <div className={styles.reportList}>
              {activeReportRows.length > 0 ? (
                <div className={styles.reportGroup}>
                  <div className={styles.reportGroupHeader}>Active Courses</div>
                  {renderReportRows(activeReportRows)}
                </div>
              ) : null}

              {previousReportRows.length > 0 ? (
                <div className={styles.reportGroup}>
                  <div className={styles.reportGroupHeader}>Previous Courses</div>
                  {renderReportRows(previousReportRows)}
                </div>
              ) : null}
            </div>
          );
        })()}
      </section>

      <AttendanceCorrectionModal
        isOpen={Boolean(correctionModal)}
        sessionTitle={correctionModal?.title}
        sessionDateLabel={correctionModal ? formatDateLabel(correctionModal.date) : ''}
        currentStatus={correctionModal?.currentStatus}
        requestedStatus={correctionModal?.currentStatus === 'present' ? 'absent' : 'present'}
        reason={correctionReason}
        onReasonChange={setCorrectionReason}
        submitting={correctionSubmitting}
        onClose={() => setCorrectionModal(null)}
        onSubmit={submitCorrection}
      />
    </div>
  );
}

export default MyAttendance;
